// src/components/Login.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import { CONFIG } from '../config';
import '../styles/tailwind.css';
import '../styles/styles.css';
import fleet_truck_image from '../assets/img/fleet_truck.jpg';
import google_login_image from '../assets/img/g.png';
import posthog from 'posthog-js';

const Login = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });

  const location = useLocation();
  const locationMessage = location.state?.message || '';
  const locationMessageType = location.state?.type || 'success';
  const messageClass = locationMessageType === 'error' ? 'text-red-600' : 'text-green-600';

  useEffect(() => {
    const token = localStorage.getItem("tensor-auth-token");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrors({ email: '', password: '' });

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    // 🔵 Track login attempt
    posthog.capture('attempted_login', {
      email: trimmedEmail,
    });

    const newErrors = {};
    if (!trimmedEmail) newErrors.email = "Please enter your email.";
    if (!trimmedPassword) newErrors.password = "Please enter your password.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/${CONFIG.LOGIN_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // 🔵 Track login success
        posthog.capture('user_logged_in');
        posthog.identify(data.user_id || trimmedEmail, {
          email: trimmedEmail,
          first_name: data.first_name,
          last_name: data.last_name,
          organisation: data.organisation_name,
        });

        localStorage.setItem("tensor-auth-token", data.access_token || "dummy-token");
        localStorage.setItem("tensor-user-id", data.user_id || "0");
        localStorage.setItem("tensor-user-first-name", data.first_name || "NA");
        localStorage.setItem("tensor-user-last-name", data.last_name);
        localStorage.setItem("tensor-user-organisation-name", data.organisation_name);
        localStorage.setItem("tensor-default-vehicle-id", data.default_vehicle_id || "0");
        localStorage.removeItem("fleet-selected-date");

        setIsLoggedIn(true);
        navigate('/dashboard');
      } else {
        // 🔵 Track failed login
        posthog.capture('login_failed', {
          email: trimmedEmail,
          message: data.message || 'Invalid email or password',
        });

        setError(data.message || 'Invalid email or password.');
        setLoading(false);
      }
    } catch (err) {
      posthog.capture('login_failed', {
        email: trimmedEmail,
        message: 'Network or server error',
      });

      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="body_bg_color overflow-hidden">
      <Header />
      <div className="min-h-[calc(100vh-70px)] flex">
        <div className="flex w-full overflow-hidden items-stretch">

          {/* Left Image */}
          <div className="hidden md:flex md:w-1/2 h-auto">
            <img 
              src={fleet_truck_image} 
              alt="Truck Fleet"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right Form */}
          <div className="w-full md:w-1/2 bg-blue-50 p-10 flex flex-col justify-center items-center">

            <div className="mb-10 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">AI-Powered Predictive Maintenance</h2>
              <p className="text-gray-700">
                Prevent failures. Boost uptime. Save costs.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              Sign in to your account
            </h3>

            <form className="p-4 md:p-0 w-[250px]" onSubmit={handleSubmit}>
              {locationMessage && (
                <div className="mb-4">
                  <p className={`${messageClass} text-sm text-center mb-4`}>{locationMessage}</p>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm mb-1 text-gray-700 text-left">Username</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  placeholder="email@email.com"
                  className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500`}
                  data-attr="login_email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm mb-1 text-gray-700 text-left">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  placeholder="Password"
                  className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${errors.password ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500`}
                  data-attr="login_password"
                />
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              {error && <p className="text-red-500 text-sm mt-1 text-center">{error}</p>}

              <div>
                <button
                  type="submit"
                  className="w-full bg-blue-700 mt-5 hover:bg-blue-800 text-white font-semibold py-2 rounded"
                  disabled={loading}
                  data-attr="login_button"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
